import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Phone, ArrowRight, RefreshCw, PhoneCall, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  // Phase 2: Call verification state
  const [callLoading, setCallLoading] = useState(false);
  const [callStatus, setCallStatus] = useState(''); // '', 'calling', 'polling', 'verified', 'failed', 'expired', 'rate_limited'
  const [verificationId, setVerificationId] = useState('');
  const pollingRef = useRef(null);
  const pollCountRef = useRef(0);
  
  // Phase 4: Rate limiting state
  const [callCooldown, setCallCooldown] = useState(0);
  const [rateLimitMessage, setRateLimitMessage] = useState('');

  // Anti-abuse: device fingerprint
  const [fingerprint, setFingerprint] = useState('');

  // Initialize device fingerprint (invisible, no UI impact)
  useEffect(() => {
    const initFingerprint = async () => {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        setFingerprint(result.visitorId);
      } catch (err) {
        // Fingerprint is optional, don't block login
      }
    };
    initFingerprint();
  }, []);

  // Phase 4: Cooldown timer effect
  useEffect(() => {
    if (callCooldown > 0) {
      const timer = setTimeout(() => setCallCooldown(callCooldown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (callCooldown === 0 && callStatus === 'rate_limited') {
      // Reset status when cooldown ends
      setCallStatus('');
      setRateLimitMessage('');
    }
  }, [callCooldown, callStatus]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Phase 3: Complete verification and login
  const completeVerificationAndLogin = async (vId) => {
    try {
      const response = await axios.post(`${API}/auth/verify-by-call/complete`, {
        verification_id: vId
      }, { withCredentials: true });

      if (response.data.success) {
        toast.success('Login successful! Redirecting...');
        // Redirect to intended page or home
        setTimeout(() => {
          window.location.href = redirectTo;
        }, 500);
        return true;
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    }
    return false;
  };

  // Phase 2: Poll verification status
  const pollVerificationStatus = (vId) => {
    pollCountRef.current = 0;
    
    pollingRef.current = setInterval(async () => {
      pollCountRef.current += 1;
      
      // Stop after 15 attempts (30 seconds at 2s interval)
      if (pollCountRef.current > 15) {
        clearInterval(pollingRef.current);
        setCallStatus('expired');
        toast.error('Verification timeout. Please try again.');
        return;
      }
      
      try {
        const response = await axios.get(`${API}/phone/verify-by-call/status?verification_id=${vId}`);
        
        if (response.data.status === 'verified') {
          clearInterval(pollingRef.current);
          setCallStatus('verified');
          toast.success('Phone verified! Logging you in...');
          
          // Phase 3: Complete login and redirect
          await completeVerificationAndLogin(vId);
        } else if (response.data.status === 'expired') {
          clearInterval(pollingRef.current);
          setCallStatus('expired');
          toast.error('Verification expired. Please try again.');
        }
        // Keep polling if still pending
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 2000); // Poll every 2 seconds
  };

  // Phase 2 + Phase 4: Verify by Call handler with rate limiting
  const handleVerifyByCall = async () => {
    // Validate phone
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    // Phase 4: Check if in cooldown
    if (callCooldown > 0) {
      toast.error(`Please wait ${callCooldown} seconds before trying again`);
      return;
    }

    // Reset state
    setCallLoading(true);
    setCallStatus('calling');
    setVerificationId('');
    setRateLimitMessage('');
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    try {
      const response = await axios.post(`${API}/phone/verify-by-call/start`, {
        phone: `+91${cleanPhone}`
      });

      if (response.data.ok) {
        setVerificationId(response.data.verification_id);
        setCallStatus('polling');
        
        // Check if session was reused (Phase 4)
        if (response.data.reused) {
          toast.info('Verification already in progress. Check your phone.');
        } else {
          toast.success('Call initiated. Check your phone.');
        }
        
        // Start polling for verification status
        pollVerificationStatus(response.data.verification_id);
      } else {
        setCallStatus('failed');
        toast.error(response.data.error || 'Unable to place call. Please try again.');
      }
    } catch (error) {
      // Phase 4: Handle rate limiting (429 response)
      if (error.response && error.response.status === 429) {
        const data = error.response.data;
        const retryAfter = data.retry_after_seconds || 60;
        
        setCallStatus('rate_limited');
        setCallCooldown(retryAfter);
        setRateLimitMessage(data.message || `Please wait ${retryAfter} seconds before trying again.`);
        toast.error(data.message || 'Rate limited. Please wait before trying again.');
      } else {
        setCallStatus('failed');
        toast.error('Unable to place call. Please try again.');
      }
    } finally {
      setCallLoading(false);
    }
  };

  // --- Anti-abuse: lazy reCAPTCHA v3 loader ---
  const loadRecaptcha = () => {
    return new Promise((resolve) => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => resolve(window.grecaptcha));
        return;
      }
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.onload = () => {
        window.grecaptcha.ready(() => resolve(window.grecaptcha));
      };
      document.head.appendChild(script);
    });
  };

  const getCaptchaToken = async () => {
    if (!RECAPTCHA_SITE_KEY) return null;
    try {
      const grecaptcha = await loadRecaptcha();
      return await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'otp_request' });
    } catch (err) {
      return null;
    }
  };

  const requestOtpApi = async (phoneNum, captchaToken = null) => {
    const payload = { phone: phoneNum, device_fingerprint: fingerprint };
    if (captchaToken) payload.captcha_token = captchaToken;

    const response = await axios.post(`${API}/auth/otp/request`, payload);

    // If CAPTCHA required, transparently get token and retry
    if (response.data.captcha_required && !captchaToken) {
      const token = await getCaptchaToken();
      if (token) {
        return await axios.post(`${API}/auth/otp/request`, {
          phone: phoneNum,
          device_fingerprint: fingerprint,
          captcha_token: token
        });
      }
    }

    return response;
  };

  const isAdminPhone = phone.replace(/\D/g, '') === '7620715987';

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    // Validate phone
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    try {
      const response = await requestOtpApi(`+91${cleanPhone}`);
      
      if (response.data.success) {
        toast.success('OTP sent successfully!');
        setStep('otp');
        // Admin: no cooldown. Others: use resend_after from response
        const resendDelay = response.data.is_admin ? 0 : (response.data.resend_after || 60);
        setResendTimer(resendDelay);
        // Store devOtp if present (dev mode only)
        if (response.data.devOtp) {
          setDevOtp(response.data.devOtp);
        } else {
          setDevOtp(''); // Clear any previous dev OTP
        }
      } else {
        // Handle rate limiting response — admin gets no cooldown on failure
        if (response.data.retry_after && !response.data.is_admin) {
          setResendTimer(response.data.retry_after);
        }
        // Admin: silent auto-retry once
        if (response.data.is_admin || cleanPhone === '7620715987') {
          toast.info('Retrying OTP...');
          try {
            const retry = await requestOtpApi(`+91${cleanPhone}`);
            if (retry.data.success) {
              toast.success('OTP sent successfully!');
              setStep('otp');
              setResendTimer(0);
              if (retry.data.devOtp) setDevOtp(retry.data.devOtp);
              return;
            }
          } catch (_) { /* fall through to show original error */ }
        }
        toast.error(response.data.message || 'Failed to send OTP');
      }
    } catch (error) {
      const message = error.response?.data?.detail || error.response?.data?.message || 'Failed to send OTP';
      // Admin: silent auto-retry on network/server errors
      if (phone.replace(/\D/g, '') === '7620715987') {
        toast.info('Retrying OTP...');
        try {
          const retry = await requestOtpApi(`+91${phone.replace(/\D/g, '')}`);
          if (retry.data.success) {
            toast.success('OTP sent successfully!');
            setStep('otp');
            setResendTimer(0);
            if (retry.data.devOtp) setDevOtp(retry.data.devOtp);
            return;
          }
        } catch (_) { /* fall through */ }
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      toast.error('Please enter 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      // Check for pending referral code
      const pendingReferralCode = localStorage.getItem('pending_referral_code');
      
      const payload = { phone, otp };
      if (pendingReferralCode) {
        payload.referral_code = pendingReferralCode;
      }
      
      const response = await axios.post(`${API}/auth/otp/verify`, payload, { withCredentials: true });
      
      if (response.data.success) {
        // Clear pending referral code after successful signup
        if (pendingReferralCode) {
          localStorage.removeItem('pending_referral_code');
        }
        
        toast.success('Login successful!');
        
        // Redirect to intended page or home
        window.location.href = redirectTo;
      } else {
        toast.error(response.data.message || 'Invalid OTP');
      }
    } catch (error) {
      const message = error.response?.data?.detail || error.response?.data?.message || 'Verification failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    // Admin: no cooldown. Others: respect timer
    if (resendTimer > 0 && !isAdminPhone) return;
    
    setLoading(true);
    try {
      const response = await requestOtpApi(phone);
      
      if (response.data.success) {
        toast.success('OTP resent successfully!');
        setResendTimer(response.data.is_admin ? 0 : 30);
        setOtp('');
        // Store devOtp if present (dev mode only), otherwise clear it
        if (response.data.devOtp) {
          setDevOtp(response.data.devOtp);
        } else {
          setDevOtp('');
        }
      } else {
        toast.error(response.data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-green-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Branding */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-2">FoodMatrix</h1>
          <p className="text-gray-600">India's first zero-commission food delivery platform</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 animate-fade-in">
          {step === 'phone' ? (
            <form onSubmit={handleSendOTP} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mobile Number
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 font-medium">
                    +91
                  </span>
                  <Input
                    type="tel"
                    placeholder="Enter 10-digit mobile number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="pl-14 pr-4 py-6 text-lg"
                    required
                    data-testid="phone-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  You'll receive a 6-digit OTP on this number
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading || phone.length !== 10}
                className="w-full btn-primary text-white py-6 rounded-xl font-semibold text-lg flex items-center justify-center gap-2"
                data-testid="send-otp-button"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  <>
                    Send OTP
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </Button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              {/* Verify by Call Button - Phase 2 + Phase 4 */}
              <Button
                type="button"
                onClick={handleVerifyByCall}
                disabled={callLoading || phone.length !== 10 || callStatus === 'verified' || callStatus === 'polling' || callCooldown > 0}
                className={`w-full py-6 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 ${
                  callStatus === 'verified' 
                    ? 'bg-green-100 text-green-700 border-2 border-green-400' 
                    : callStatus === 'polling'
                    ? 'bg-blue-50 text-blue-700 border border-blue-300'
                    : callStatus === 'rate_limited' || callCooldown > 0
                    ? 'bg-orange-50 text-orange-600 border border-orange-300'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
                }`}
                data-testid="verify-by-call-button"
              >
                {callLoading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Calling you…
                  </>
                ) : callStatus === 'polling' ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Waiting for verification...
                  </>
                ) : callStatus === 'verified' ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Verified ✓
                  </>
                ) : callCooldown > 0 ? (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    Wait {callCooldown}s
                  </>
                ) : (
                  <>
                    <PhoneCall className="w-5 h-5" />
                    Verify by Call
                  </>
                )}
              </Button>
              {callStatus === 'failed' && (
                <p className="text-sm text-red-500 text-center">
                  Unable to place call. Please try again.
                </p>
              )}
              {callStatus === 'expired' && (
                <p className="text-sm text-red-500 text-center">
                  Verification failed. Please try again.
                </p>
              )}
              {(callStatus === 'rate_limited' || rateLimitMessage) && (
                <p className="text-sm text-orange-600 text-center">
                  {rateLimitMessage || 'Rate limited. Please wait before trying again.'}
                </p>
              )}
              {callStatus === 'verified' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                  <p className="text-sm text-green-700 font-medium">
                    Phone verified! Logging you in...
                  </p>
                </div>
              )}
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Enter OTP
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  Sent to +91 {phone}
                  <button
                    type="button"
                    onClick={() => setStep('phone')}
                    className="text-green-600 ml-2 hover:underline"
                    data-testid="change-number-button"
                  >
                    Change
                  </button>
                </p>
                <Input
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-widest py-6"
                  required
                  maxLength={6}
                  data-testid="otp-input"
                />
                {devOtp && process.env.NODE_ENV === 'development' && (
                  <p className="text-xs text-orange-600 mt-2 font-mono bg-orange-50 p-2 rounded" data-testid="dev-otp-display">
                    DEV OTP: {devOtp}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full btn-primary text-white py-6 rounded-xl font-semibold text-lg"
                data-testid="verify-otp-button"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify OTP'
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={(resendTimer > 0 && !isAdminPhone) || loading}
                  className={`text-sm font-medium ${
                    resendTimer > 0 && !isAdminPhone
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-green-600 hover:underline'
                  }`}
                  data-testid="resend-otp-button"
                >
                  {resendTimer > 0 && !isAdminPhone ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
